import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const UnderstandingRoofingMaterialsPage = () => {
  const handleCallNow = () => {
    window.location.href = 'tel:252-371-2266';
  };

  const handleGetEstimate = () => {
    window.location.href = '/contact';
  };

  return (
    <Layout>
      <Helmet>
        <title>Roofing Materials Guide | Carolina Trophy Roofs</title>
        <meta name="description" content="Discover the best roofing materials for North Carolina homes. Compare asphalt, metal, slate & more. Expert advice from Carolina Trophy Roofs. Free estimates available." />
      </Helmet>
      
      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              🛠️ Understanding Roofing Materials: The Ultimate Guide for Homeowners in North Carolina
            </h1>
          </div>

          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">🧠 Introduction: Why Your Roof Material Choice Matters</h2>
            <p className="text-lg text-gray-700 mb-4">
              Your roof is more than a shield—it's your home's first line of defense, your insulation partner, and your curb appeal hero. But with rising storm frequency in North Carolina and fluctuating material costs, making the wrong roofing choice could cost you thousands. At <strong>Carolina Trophy Roofs</strong>, we help homeowners make <em>smart</em>, <em>durable</em>, and <em>financially sound</em> decisions. This guide unpacks the pros, cons, costs, and ideal use cases for the top roofing materials on the market—customized for the climate and needs of Eastern NC.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">📊 At-a-Glance Roofing Material Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left">Material</th>
                    <th className="border border-gray-300 p-3 text-left">Lifespan</th>
                    <th className="border border-gray-300 p-3 text-left">Cost/Sq Ft</th>
                    <th className="border border-gray-300 p-3 text-left">Wind Rating</th>
                    <th className="border border-gray-300 p-3 text-left">Maintenance</th>
                    <th className="border border-gray-300 p-3 text-left">Style Versatility</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">Asphalt Shingle</td>
                    <td className="border border-gray-300 p-3">15–30 years</td>
                    <td className="border border-gray-300 p-3">$3.50–$5.50</td>
                    <td className="border border-gray-300 p-3">Up to 130 mph</td>
                    <td className="border border-gray-300 p-3">Low</td>
                    <td className="border border-gray-300 p-3">High</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3">Metal</td>
                    <td className="border border-gray-300 p-3">40–70 years</td>
                    <td className="border border-gray-300 p-3">$7.00–$12.00</td>
                    <td className="border border-gray-300 p-3">140–160+ mph</td>
                    <td className="border border-gray-300 p-3">Low</td>
                    <td className="border border-gray-300 p-3">Moderate</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Slate</td>
                    <td className="border border-gray-300 p-3">75–150 years</td>
                    <td className="border border-gray-300 p-3">$15.00–$30.00</td>
                    <td className="border border-gray-300 p-3">160+ mph</td>
                    <td className="border border-gray-300 p-3">Low</td>
                    <td className="border border-gray-300 p-3">High</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3">Clay Tile</td>
                    <td className="border border-gray-300 p-3">50–100 years</td>
                    <td className="border border-gray-300 p-3">$10.00–$18.00</td>
                    <td className="border border-gray-300 p-3">125–150 mph</td>
                    <td className="border border-gray-300 p-3">Medium</td>
                    <td className="border border-gray-300 p-3">High</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Synthetic/Composite</td>
                    <td className="border border-gray-300 p-3">30–50 years</td>
                    <td className="border border-gray-300 p-3">$8.00–$14.00</td>
                    <td className="border border-gray-300 p-3">110–150 mph</td>
                    <td className="border border-gray-300 p-3">Low</td>
                    <td className="border border-gray-300 p-3">High</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3">Wood Shake</td>
                    <td className="border border-gray-300 p-3">20–40 years</td>
                    <td className="border border-gray-300 p-3">$6.00–$10.00</td>
                    <td className="border border-gray-300 p-3">90–110 mph</td>
                    <td className="border border-gray-300 p-3">High</td>
                    <td className="border border-gray-300 p-3">Rustic appeal</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Material Sections */}
          <div className="space-y-8 mb-12">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🏠 1. Asphalt Shingles: America's Favorite Roof</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2">✅ Pros:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Affordable and quick to install</li>
                      <li>Great for North Carolina's mixed climate</li>
                      <li>Available in a wide range of colors and styles</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-orange-600 mb-2">⚠️ Cons:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Shorter lifespan than other materials</li>
                      <li>Vulnerable to algae if not algae-resistant</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 font-medium">Best for: Homeowners who want reliable performance on a budget.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🔩 2. Metal Roofing: Storm-Proof and Sustainable</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Metal roofs are rising in popularity—and for good reason. They resist high winds, fire, and even moss growth.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2">✅ Pros:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Long lifespan (50+ years)</li>
                      <li>Energy-efficient and recyclable</li>
                      <li>Performs exceptionally in wind-prone zones</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-orange-600 mb-2">⚠️ Cons:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Higher upfront cost</li>
                      <li>Can be noisy during storms if not insulated</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm">🧠 <strong>Fun Fact:</strong> Metal reflects solar radiant heat, reducing cooling costs by 10-25%</p>
                </div>
                <p className="mt-4 font-medium">Best for: Long-term homeowners, storm-prone areas, and eco-conscious buyers.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🪨 3. Slate Roofing: The Timeless Powerhouse</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Slate is one of the most beautiful and durable roofing materials—but it comes with a luxury price tag.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2">✅ Pros:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Unmatched durability (up to 150 years!)</li>
                      <li>Adds high-end architectural value</li>
                      <li>Extremely fire and storm resistant</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-orange-600 mb-2">⚠️ Cons:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Expensive and heavy (requires structural support)</li>
                      <li>Requires experienced installers</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 font-medium">Best for: Historic homes or upscale custom properties.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🌪️ 4. Synthetic Roofing: A Smart Middle Ground</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Manufacturers like DaVinci and Brava make composite shingles that mimic wood, slate, or tile—at a fraction of the weight and cost.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2">✅ Pros:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Lightweight but strong</li>
                      <li>Weather-resistant and mold-proof</li>
                      <li>Custom colors and textures</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-orange-600 mb-2">⚠️ Cons:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Still relatively new—long-term performance studies are ongoing</li>
                      <li>Can vary in quality</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 font-medium">Best for: Homeowners who want style and durability without slate or clay costs.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🌳 5. Wood Shake Roofs: Rustic, But High Maintenance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">While aesthetically charming, wood shake roofs are less common due to their maintenance needs and fire risk.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2">✅ Pros:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Rustic beauty and natural insulation</li>
                      <li>Biodegradable</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-orange-600 mb-2">⚠️ Cons:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Susceptible to rot, mold, and fire</li>
                      <li>Requires regular treatment and cleaning</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 font-medium">Best for: Vacation homes or buyers in non-humid regions.</p>
              </CardContent>
            </Card>
          </div>

          {/* Local Considerations */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">📍 Local Considerations for NC Roof Buyers</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Humidity & Rain:</strong> Choose algae-resistant shingles or metal roofing.</li>
              <li><strong>Storm Season:</strong> Prioritize wind-rated products (Class 4 impact rating preferred).</li>
              <li><strong>HOA Rules:</strong> Verify appearance and material compliance before selecting.</li>
            </ul>
          </div>

          {/* Why Trust Carolina Trophy Roofs */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">👷 Why Homeowners Trust Carolina Trophy Roofs</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Local storm experience</li>
              <li>Certified GAF installers</li>
              <li>5-star Google reputation</li>
              <li>Detailed warranty options</li>
              <li>Free estimates, flexible financing</li>
              <li>Call or text anytime: <strong>(252) 371-2266</strong></li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">🧾 Frequently Asked Questions</h2>
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What is the most cost-effective roofing material?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Asphalt shingles are generally the most affordable and widely used option.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What is the longest-lasting roof material?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Natural slate can last 100+ years when properly installed and maintained.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What roof is best for hurricane-prone areas?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Metal or synthetic composite roofs rated for high winds and impact resistance are best.</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Citations Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Sources & Further Reading</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>
                  1. National Roofing Contractors Association - <a href="https://www.nrca.net/consumer/materials" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Roofing Materials Guide</a>
                </p>
                <p>
                  2. Energy.gov - <a href="https://www.energy.gov/energysaver/energy-efficient-home-design/cool-roofs" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Cool Roofs and Energy Efficiency</a>
                </p>
                <p>
                  3. Insurance Information Institute - <a href="https://www.iii.org/article/protecting-your-home-from-wind-damage" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Wind Resistance in Roofing Materials</a>
                </p>
                <p>
                  4. FEMA - <a href="https://www.fema.gov/emergency-managers/risk-management/building-science/bsc-bulletins/high-wind-residential-construction" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">High Wind Resistant Construction Guidelines</a>
                </p>
                <p>
                  5. GAF - <a href="https://www.gaf.com/en-us/roofing-guide/choosing-the-right-shingle" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Choosing the Right Roofing Shingle</a>
                </p>
                <p>
                  6. Metal Roofing Alliance - <a href="https://www.metalroofing.com/benefits/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Benefits of Metal Roofing</a>
                </p>
                <p>
                  7. This Old House - <a href="https://www.thisoldhouse.com/roofing/21018307/all-about-roofing-materials" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">All About Roofing Materials</a>
                </p>
                <p>
                  8. HomeAdvisor - <a href="https://www.homeadvisor.com/cost/roofing/install-roof/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Roofing Materials Cost Guide</a>
                </p>
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>Disclaimer:</strong> This information is for educational purposes. Always consult with roofing professionals for specific advice about your property.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center bg-brand-carolina-blue text-white p-8 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">📞 Ready to Choose the Perfect Roof?</h2>
            <p className="text-lg mb-6">
              Choosing the right roof is a big decision. Let us make it simple.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleCallNow}
                className="bg-brand-safety-orange text-white hover:bg-brand-safety-orange/90"
              >
                <Phone className="h-5 w-5 mr-2" />
                Call (252) 371-2266
              </Button>
              <Button 
                onClick={handleGetEstimate}
                variant="outline"
                className="bg-white text-brand-carolina-blue hover:bg-gray-100"
              >
                <Mail className="h-5 w-5 mr-2" />
                Request Free Estimate
              </Button>
            </div>
          </div>

          {/* Related Resources */}
          <div className="mt-12 mb-8">
            <h2 className="text-2xl font-bold mb-6 text-center">📚 Related Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">
                    <a href="/resources/roof-ventilation-guide" className="hover:text-primary text-brand-carolina-blue">
                      Complete Roof Ventilation Guide
                    </a>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Learn how proper ventilation affects your roofing material performance and extends system life.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">
                    <a href="/resources/roof-replacement-process" className="hover:text-primary text-brand-carolina-blue">
                      Roof Replacement Process
                    </a>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Step-by-step guide to roof replacement and material installation best practices.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">
                    <a href="/resources/manufacturer-warranties" className="hover:text-primary text-brand-carolina-blue">
                      Manufacturer Warranties
                    </a>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Understand warranty coverage for different roofing materials and what affects your protection.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Final Thoughts */}
          <div className="mt-12 text-center">
            <h2 className="text-2xl font-bold mb-4">🔍 Final Thoughts</h2>
            <p className="text-lg text-gray-700 mb-4">
              Your roof isn't just a surface—it's a system. It insulates your family, protects your investment, and defines your curb appeal. Use this guide to make informed, confident choices—and let <strong>Carolina Trophy Roofs</strong> bring expertise, reliability, and integrity to your home.
            </p>
            <p className="text-xl font-bold text-brand-carolina-blue">
              ☎️ Ready to get started? Call us now at (252) 371-2266.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnderstandingRoofingMaterialsPage;
